import React, { useState } from 'react';
import { useContent } from '../contexts/ContentContext';
import FormationSelect from './FormationSelect';
import CountryCityFields from './CountryCityFields';
import { resolveDefaultCountry } from '../utils/locationData';
import { prepareLeadSubmitTracking } from '../utils/metaPixel';
import { resolveTestimonialVideo } from '../utils/testimonialVideo';
import TestimonialVideoBlock from './TestimonialVideoBlock';
import { getMediaUrl } from '../utils/mediaUrl';
import { validateYouTubeUrl } from '../utils/youtubeUtils';

const TestimonialCarouselCard = ({ item, type, expertFormationLabel, participantAboutLabel, videoBadgeText }) => {
  const isExpert = type === 'expert';
  const photoClass = isExpert ? 'expert-photo' : 'participant-photo';
  const infoClass = isExpert ? 'expert-info' : 'participant-info';
  const cardClass = isExpert ? 'expert-testimonial' : 'participant-testimonial';

  const video = resolveTestimonialVideo(item);

  return (
    <div className={`testimonial-card ${cardClass}`}>
      <div className="testimonial-header">
        <div className={photoClass}>
          {item.photo ? (
            <img src={item.photo} alt={item.name} />
          ) : (
            <i className={`fas ${isExpert ? 'fa-user-md' : 'fa-user-doctor'}`}></i>
          )}
        </div>
        <div className={infoClass}>
          <h4>{item.name}</h4>
          <span className={isExpert ? 'expert-title' : 'participant-title'}>{item.title}</span>
          <span className={isExpert ? 'expert-formation' : 'participant-formation'}>
            {isExpert
              ? `${expertFormationLabel || 'Formation'}: ${item.formation}`
              : `${participantAboutLabel || 'À propos'}: ${item.formation}`}
          </span>
          {!isExpert && item.location && (
            <span className="participant-location">
              <i className="fas fa-map-marker-alt"></i>
              {item.location} • {item.date}
            </span>
          )}
        </div>
        {item.type === 'video' && video && (
          <div className="video-indicator">
            <i className="fab fa-youtube"></i>
            <span>{videoBadgeText || 'Témoignage Vidéo'}</span>
          </div>
        )}
      </div>
      <div className="testimonial-content">
        {item.type === 'video' && video ? (
          <TestimonialVideoBlock item={item} name={item.name} />
        ) : (
          <div className="testimonial-text">
            {item.text?.trim() ? (
              <p>"{item.text}"</p>
            ) : item.type === 'video' ? (
              <p className="testimonial-video-placeholder">
                <i className="fab fa-youtube"></i> Témoignage vidéo — {item.formation || 'formation DentoXcellence'}
              </p>
            ) : null}
          </div>
        )}
        {isExpert ? (
          <div className="expert-specialties">
            {(item.specialties || []).map((specialty, specialtyIndex) => (
              <span key={specialtyIndex} className="specialty-badge">{specialty}</span>
            ))}
          </div>
        ) : (
          <div className="testimonial-rating">
            {[...Array(item.rating || 5)].map((_, starIndex) => (
              <i key={starIndex} className="fas fa-star"></i>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Testimonials = () => {
  const { getContent } = useContent();
  const pageHeader = getContent('testimonials.pageHeader') || {};
  const submitSection = getContent('testimonials.submitSection') || {};
  const expertTestimonials = getContent('home.expertTestimonials') || {};
  const participantTestimonials = getContent('home.participantTestimonials') || {};
  const contactForm = getContent('contact.contactForm') || {};

  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rating, setRating] = useState(5);
  const [testimonialType, setTestimonialType] = useState('text');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(
    () => resolveDefaultCountry(contactForm.defaultCountry)
  );
  const [cityKey, setCityKey] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    const formData = new FormData(e.target);
    const city = formData.get('city');
    const country = formData.get('country');
    const message = formData.get('text') || '';
    const formation = formData.get('formation');

    if (testimonialType === 'video') {
      if (!validateYouTubeUrl(youtubeUrl)) {
        setFormStatus({ type: 'error', message: 'Veuillez entrer une URL YouTube valide pour votre témoignage vidéo.' });
        setIsSubmitting(false);
        return;
      }
    } else if (!message.trim()) {
      setFormStatus({ type: 'error', message: 'Veuillez rédiger votre témoignage.' });
      setIsSubmitting(false);
      return;
    }

    const data = {
      name: formData.get('name'),
      city: [city, country].filter(Boolean).join(', '),
      formation,
      courseId: selectedCourseId,
      rating: parseInt(formData.get('rating'), 10) || 5,
      message,
      testimonialType,
      youtube_url: testimonialType === 'video' ? youtubeUrl : '',
    };

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://dentoxcellence-backend.dentoxcellence.workers.dev/api';
      const metaTracking = prepareLeadSubmitTracking({
        formationName: data.formation,
        source: 'testimonials_form',
      });
      const response = await fetch(`${API_URL}/testimonials/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, ...metaTracking }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setFormStatus({
          type: 'success',
          message: submitSection.successMessage || 'Merci pour votre témoignage ! Il sera publié après validation par notre équipe.'
        });
        e.target.reset();
        setRating(5);
        setTestimonialType('text');
        setYoutubeUrl('');
        setSelectedCourseId('');
      } else {
        throw new Error(result.message || result.error || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Testimonial submission error:', error);
      setFormStatus({
        type: 'error',
        message: submitSection.errorMessage || 'Une erreur est survenue. Veuillez réessayer plus tard.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const expertItems = expertTestimonials.testimonials || [];
  const participantItems = participantTestimonials.testimonials || [];
  const cardLabels = {
    expertFormationLabel: submitSection.expertFormationLabel,
    participantAboutLabel: submitSection.participantAboutLabel,
    videoBadgeText: submitSection.videoBadgeText,
  };

  return (
    <div className="testimonials-page">
      <section className="page-header" style={{ position: 'relative', overflow: 'hidden' }}>
        {pageHeader.heroImage && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            zIndex: 0
          }}>
            <img
              src={getMediaUrl(pageHeader.heroImage)}
              alt="Hero"
              style={pageHeader.heroImageSettings ? {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(calc(-50% + ${pageHeader.heroImageSettings.position.x}px), calc(-50% + ${pageHeader.heroImageSettings.position.y}px)) scale(${pageHeader.heroImageSettings.scale})`,
                transformOrigin: 'center center',
                width: 'auto',
                height: 'auto',
                minWidth: '100%',
                minHeight: '100%',
                maxWidth: 'none',
                objectFit: 'cover'
              } : {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
        )}

        {pageHeader.overlayEnabled !== false && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: pageHeader.overlayColor || '#58c4dc',
            opacity: pageHeader.overlayOpacity ?? 0.85,
            zIndex: 1
          }}></div>
        )}

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="page-header-content">
            <h1>{pageHeader.title || 'Témoignages'}</h1>
            <p>{pageHeader.subtitle || 'Découvrez les expériences de nos formateurs et participants, et partagez la vôtre'}</p>
          </div>
        </div>
      </section>

      <section className="expert-testimonials" id="expert-testimonials">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{expertTestimonials.title || 'Témoignages de nos Experts'}</h2>
            <p className="section-subtitle">
              {expertTestimonials.subtitle || 'Découvrez l\'expertise et l\'expérience de nos formateurs de renommée internationale'}
            </p>
          </div>
        </div>
        <div className="testimonials-carousel-wrapper">
          <div className="testimonials-carousel testimonials-carousel-experts">
            {expertItems.map((expert) => (
              <TestimonialCarouselCard key={`expert-${expert.id}`} item={expert} type="expert" {...cardLabels} />
            ))}
            {expertItems.map((expert) => (
              <TestimonialCarouselCard key={`expert-dup-${expert.id}`} item={expert} type="expert" {...cardLabels} />
            ))}
          </div>
        </div>
      </section>

      <section className="participant-testimonials" id="participant-testimonials">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{participantTestimonials.title || 'Témoignages de nos Participants'}</h2>
            <p className="section-subtitle">
              {participantTestimonials.subtitle || 'Découvrez comment nos formations ont transformé la carrière de dentistes à travers l\'Algérie'}
            </p>
          </div>
        </div>
        <div className="testimonials-carousel-wrapper">
          <div className="testimonials-carousel testimonials-carousel-participants">
            {participantItems.map((participant) => (
              <TestimonialCarouselCard key={`participant-${participant.id}`} item={participant} type="participant" {...cardLabels} />
            ))}
            {participantItems.map((participant) => (
              <TestimonialCarouselCard key={`participant-dup-${participant.id}`} item={participant} type="participant" {...cardLabels} />
            ))}
          </div>
        </div>
      </section>

      <section className="testimonial-submit-section" id="submit">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{submitSection.title || 'Partagez Votre Expérience'}</h2>
            <p className="section-subtitle">
              {submitSection.subtitle || 'Vous avez suivi une formation DentoXcellence ? Votre témoignage compte pour nous et inspire d\'autres praticiens.'}
            </p>
          </div>

          <form className="testimonial-submit-form contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="testimonial-name">Nom complet *</label>
                <input type="text" id="testimonial-name" name="name" required placeholder="Dr. Prénom Nom" />
              </div>
              <CountryCityFields
                contactForm={contactForm}
                renderCity={false}
                countryRequired
                countryId="testimonial-country"
                countryName="country"
                activeCountry={selectedCountry}
                onActiveCountryChange={(value) => {
                  setSelectedCountry(value);
                  setCityKey((k) => k + 1);
                }}
              />
            </div>

            <CountryCityFields
              contactForm={contactForm}
              renderCountry={false}
              cityRequired
              cityName="city"
              cityId="testimonial-city"
              activeCountry={selectedCountry}
              cityResetKey={cityKey}
            />

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="testimonial-formation">Formation suivie *</label>
                <FormationSelect
                  getContent={getContent}
                  id="testimonial-formation"
                  name="formation"
                  required
                  defaultValue=""
                  onFormationChange={({ courseId }) => setSelectedCourseId(courseId || '')}
                />
              </div>
              <div className="form-group">
                <label htmlFor="testimonial-rating">Note *</label>
                <select
                  id="testimonial-rating"
                  name="rating"
                  required
                  value={rating}
                  onChange={(e) => setRating(parseInt(e.target.value, 10))}
                >
                  {[5, 4, 3, 2, 1].map((value) => (
                    <option key={value} value={value}>{value} étoile{value > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="testimonial-type">Type de témoignage *</label>
              <select
                id="testimonial-type"
                name="testimonialType"
                value={testimonialType}
                onChange={(e) => setTestimonialType(e.target.value)}
                required
              >
                <option value="text">Témoignage écrit</option>
                <option value="video">Témoignage vidéo (YouTube)</option>
              </select>
            </div>

            {testimonialType === 'video' ? (
              <div className="form-group">
                <label htmlFor="testimonial-youtube">Lien YouTube de votre témoignage *</label>
                <input
                  type="url"
                  id="testimonial-youtube"
                  name="youtube_url"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  required
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <small style={{ color: '#64748b' }}>
                  Publiez votre vidéo sur YouTube, puis collez le lien ici.
                </small>
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="testimonial-text">Votre témoignage *</label>
                <textarea
                  id="testimonial-text"
                  name="text"
                  required
                  rows={5}
                  placeholder={submitSection.messagePlaceholder || 'Décrivez votre expérience avec DentoXcellence...'}
                />
              </div>
            )}

            {testimonialType === 'video' && (
              <div className="form-group">
                <label htmlFor="testimonial-text-optional">Commentaire (optionnel)</label>
                <textarea
                  id="testimonial-text-optional"
                  name="text"
                  rows={3}
                  placeholder="Quelques mots pour accompagner votre vidéo..."
                />
              </div>
            )}

            {formStatus.message && (
              <div className={`form-status ${formStatus.type}`}>
                <i className={`fas ${formStatus.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                {formStatus.message}
              </div>
            )}

            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  {submitSection.submittingText || 'Envoi en cours...'}
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i>
                  {submitSection.submitText || 'Envoyer mon témoignage'}
                </>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
